import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Download,
  Trash2,
  Pencil,
  FileText,
  Receipt as ReceiptIcon,
  ShieldCheck,
  Settings2,
  Check,
} from "lucide-react";
import { expensesApi, expenseCategoriesApi } from "@/lib/api";
import { ExpenseDialog } from "@/components/expenses/ExpenseDialog";
import { CategoriesDialog } from "@/components/expenses/CategoriesDialog";
import { ReceiptLightbox } from "@/components/expenses/ReceiptLightbox";
import { Button } from "@/components/ui/button";

function fmtUsd(cents) {
  if (cents === null || cents === undefined) return "—";
  const c = Math.abs(cents);
  const dollars = Math.floor(c / 100);
  const rem = c % 100;
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${dollars.toLocaleString()}.${String(rem).padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ProjectExpensesPage() {
  const { id } = useParams();
  const { project } = useOutletContext();
  const isAdmin = project?.my_role === "admin";
  const [data, setData] = useState({ expenses: [], summary: null });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [receiptExpense, setReceiptExpense] = useState(null);

  const reload = async () => {
    try {
      const [d, cats] = await Promise.all([
        expensesApi.list(id),
        expenseCategoriesApi.list(),
      ]);
      setData(d);
      setCategories(cats);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load expenses");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const summary = data.summary || { total_cents: 0, reimbursable_cents: 0, count: 0, by_category: [] };
  const expenses = data.expenses || [];

  // Client-side running total for the table (chronological by expense_date desc).
  const rowsWithRunning = useMemo(() => {
    // Sort ascending by date to compute running total, then reverse for display.
    const asc = expenses
      .slice()
      .sort((a, b) => (a.expense_date || "").localeCompare(b.expense_date || ""));
    let running = 0;
    const withRunning = asc.map((e) => {
      running += Number(e.amount_cents || 0);
      return { ...e, _running: running };
    });
    return withRunning.reverse();
  }, [expenses]);

  const handleDelete = async (exp) => {
    if (!window.confirm(`Delete this ${fmtUsd(exp.amount_cents)} expense at ${exp.merchant}?`)) return;
    try {
      await expensesApi.remove(id, exp.id);
      toast.success("Expense deleted");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleExportPdf = async () => {
    setDownloading(true);
    try {
      const blob = await expensesApi.downloadPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project.name || "expenses").replace(/\s+/g, "-").toLowerCase()}-expenses.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16 text-center">
        <ShieldCheck className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
        <div className="text-overline text-zinc-500 mb-2">Admin Only</div>
        <h2 className="font-heading text-2xl font-light text-zinc-200">
          Expenses are restricted
        </h2>
        <p className="text-sm text-zinc-500 mt-2">
          Only project admins can view or log expenses for this project.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="project-expenses-page"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-overline text-zinc-500 mb-1">Job Expenses</div>
          <h2 className="font-heading text-3xl font-light text-zinc-100">
            Running total
          </h2>
          <div className="flex items-baseline gap-4 mt-2 flex-wrap">
            <div
              data-testid="expenses-total"
              className="font-mono text-4xl text-amber-400 font-bold tracking-tight"
            >
              {fmtUsd(summary.total_cents)}
            </div>
            {summary.pending_reimbursement_cents > 0 && (
              <div
                data-testid="expenses-pending-reimbursement-total"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-amber-500/10 border border-amber-500/30 text-xs"
                title="Reimbursable expenses that haven't been paid back yet"
              >
                <span className="font-mono uppercase tracking-wider text-amber-400/80">Pending</span>
                <span className="font-mono text-amber-400 font-semibold">
                  {fmtUsd(summary.pending_reimbursement_cents)}
                </span>
              </div>
            )}
            {summary.reimbursed_cents > 0 && (
              <div
                data-testid="expenses-reimbursed-total"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-xs"
                title="Reimbursables already paid back"
              >
                <span className="font-mono uppercase tracking-wider text-emerald-400/80">Reimbursed</span>
                <span className="font-mono text-emerald-400 font-semibold">
                  {fmtUsd(summary.reimbursed_cents)}
                </span>
              </div>
            )}
            <div className="text-xs text-zinc-500">
              {summary.count} expense{summary.count === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="manage-categories-btn"
            variant="outline"
            onClick={() => setCatsOpen(true)}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm"
          >
            <Settings2 className="w-4 h-4 mr-2" /> Categories
          </Button>
          <Button
            data-testid="export-expenses-pdf-btn"
            variant="outline"
            onClick={handleExportPdf}
            disabled={downloading || expenses.length === 0}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Report
          </Button>
          <Button
            data-testid="log-expense-btn"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" /> Log expense
          </Button>
        </div>
      </div>

      {summary.by_category && summary.by_category.length > 0 && (
        <div
          data-testid="expenses-by-category"
          className="flex items-center gap-2 mb-4 flex-wrap"
        >
          {summary.by_category.slice(0, 8).map((row) => (
            <div
              key={row.category}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-zinc-900/60 border border-zinc-800 text-xs"
            >
              <span className="text-zinc-400">{row.category}</span>
              <span className="font-mono text-zinc-200 font-semibold">
                {fmtUsd(row.cents)}
              </span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      ) : expenses.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
          <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="text-zinc-500 text-sm mb-4">
            No expenses logged yet.
          </div>
          <Button
            data-testid="log-first-expense-btn"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" /> Log first expense
          </Button>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-900">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="expenses-table">
              <thead>
                <tr className="bg-zinc-950/80 border-b border-zinc-800">
                  <th className="text-left px-4 py-3 text-overline text-zinc-500">Date</th>
                  <th className="text-left px-4 py-3 text-overline text-zinc-500">Merchant</th>
                  <th className="text-left px-4 py-3 text-overline text-zinc-500">Category</th>
                  <th className="text-right px-4 py-3 text-overline text-zinc-500">Amount</th>
                  <th className="text-right px-4 py-3 text-overline text-zinc-500">Running</th>
                  <th className="text-center px-4 py-3 text-overline text-zinc-500">Reimbursable</th>
                  <th className="text-center px-4 py-3 text-overline text-zinc-500">Status</th>
                  <th className="text-center px-4 py-3 text-overline text-zinc-500">Receipt</th>
                  <th className="w-24 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rowsWithRunning.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`expense-row-${e.id}`}
                    className="border-b border-zinc-800/60 hover:bg-zinc-800/30"
                  >
                    <td className="px-4 py-3 text-sm text-zinc-300 whitespace-nowrap">
                      {fmtDate(e.expense_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-100">
                      {e.merchant}
                      {e.notes && (
                        <div className="text-xs text-zinc-500 mt-0.5 truncate max-w-[220px]">
                          {e.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{e.category_name}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-zinc-100 whitespace-nowrap">
                      {fmtUsd(e.amount_cents)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-amber-400/80 whitespace-nowrap">
                      {fmtUsd(e._running)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {e.reimbursable ? (
                        <span
                          data-testid={`reimbursable-flag-${e.id}`}
                          title="Reimbursable"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-400"
                        >
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        </span>
                      ) : (
                        <span className="text-zinc-700 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {e.reimbursable ? (
                        e.reimbursed_at ? (
                          <div className="flex items-center justify-center gap-1">
                            <span
                              data-testid={`reimburse-status-${e.id}`}
                              className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                              title={`Reimbursed ${fmtDate(e.reimbursed_at.slice(0,10))}`}
                            >
                              Reimbursed
                            </span>
                            <button
                              data-testid={`unmark-reimbursed-${e.id}`}
                              onClick={async () => {
                                try {
                                  await expensesApi.unmarkReimbursed(id, e.id);
                                  toast.success("Marked as pending");
                                  await reload();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed");
                                }
                              }}
                              title="Undo — mark as pending"
                              className="text-zinc-600 hover:text-zinc-300 text-[10px] px-1"
                            >
                              undo
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5">
                            <span
                              data-testid={`reimburse-status-${e.id}`}
                              className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/30"
                            >
                              Pending
                            </span>
                            <button
                              data-testid={`mark-reimbursed-${e.id}`}
                              onClick={async () => {
                                try {
                                  await expensesApi.markReimbursed(id, e.id);
                                  toast.success("Marked reimbursed");
                                  await reload();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed");
                                }
                              }}
                              title="Mark as reimbursed"
                              className="text-emerald-400 hover:text-emerald-300 text-[10px] font-mono uppercase tracking-wider hover:underline"
                            >
                              mark paid
                            </button>
                          </div>
                        )
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {e.receipt_storage_path ? (
                        <button
                          type="button"
                          data-testid={`view-receipt-${e.id}`}
                          onClick={() => setReceiptExpense(e)}
                          className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs"
                          title={e.receipt_filename}
                        >
                          <ReceiptIcon className="w-3.5 h-3.5" /> View
                        </button>
                      ) : (
                        <span className="text-zinc-700 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`edit-expense-${e.id}`}
                        onClick={() => {
                          setEditing(e);
                          setDialogOpen(true);
                        }}
                        className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`delete-expense-${e.id}`}
                        onClick={() => handleDelete(e)}
                        className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800 ml-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-zinc-950/60 border-t-2 border-zinc-700">
                  <td colSpan={3} className="px-4 py-3 text-right text-overline text-zinc-400">
                    Grand total
                  </td>
                  <td
                    colSpan={2}
                    className="px-4 py-3 text-right font-mono text-amber-400 font-bold text-base"
                    data-testid="expenses-grand-total"
                  >
                    {fmtUsd(summary.total_cents)}
                  </td>
                  <td colSpan={4} className="px-4 py-3 text-right text-xs">
                    {summary.pending_reimbursement_cents > 0 && (
                      <span className="text-amber-400 mr-3" data-testid="footer-pending">
                        {fmtUsd(summary.pending_reimbursement_cents)} pending
                      </span>
                    )}
                    {summary.reimbursed_cents > 0 && (
                      <span className="text-emerald-400" data-testid="footer-reimbursed">
                        {fmtUsd(summary.reimbursed_cents)} reimbursed
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {dialogOpen && (
        <ExpenseDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          projectId={id}
          expense={editing}
          categories={categories}
          onCategoriesChanged={reload}
          onSaved={async () => {
            setDialogOpen(false);
            setEditing(null);
            await reload();
          }}
        />
      )}

      {catsOpen && (
        <CategoriesDialog
          open={catsOpen}
          onOpenChange={setCatsOpen}
          categories={categories}
          onChanged={reload}
        />
      )}

      <ReceiptLightbox
        open={!!receiptExpense}
        onOpenChange={(v) => { if (!v) setReceiptExpense(null); }}
        projectId={id}
        expense={receiptExpense}
      />
    </div>
  );
}
